import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { FieldMapping, Order, Payload, SampledKeysArray, SimulationData, TableData } from 'src/app/core/model';
import { TemplateManagementService } from 'src/app/feature-modules/template-management-v1/service/template-management.service';
import { DataService } from '../../common-services/data.service';
import { DialogService } from '../../common-services/dialog.service';
import { SharedService } from '../../common-services/shared.service';

@Component({
  selector: 'app-preview-template-popup',
  templateUrl: './preview-template-popup.component.html',
  styleUrls: ['./preview-template-popup.component.scss']
})
export class PreviewTemplatePopupComponent implements OnInit, OnDestroy {

  tableData: TableData[] = [];

  columnHeader = {
    'key': 'Key',
    'name': 'Name',
    'type': 'Type',
    'configurations': 'Configurations',
  };

  private _createTemplateSubscription: Subscription;
  private _updateTemplateSubscription: Subscription;

  constructor(public dialogRef: MatDialogRef<PreviewTemplatePopupComponent>, @Inject(MAT_DIALOG_DATA) public data, private _templateService: TemplateManagementService, private _router: Router, private _dialogService: DialogService, private _dataService: DataService, private _sharedService: SharedService) {
  }

  ngOnInit(): void {
    this._setTableData();
  }

  ngOnDestroy() {
    this._createTemplateSubscription?.unsubscribe();
    this._updateTemplateSubscription?.unsubscribe();
  }

  /**
   * Publishes preview template popup component
   */
  publish() {
    if (this.data?.templateDetails?.selectedTenant?._id) {
      sessionStorage.setItem('deviceModuleTenantId', JSON.stringify(this.data?.templateDetails?.selectedTenant?._id));
    }
    let fieldMappings: FieldMapping[] = [];
    this.data?.simulationData?.forEach((field: SimulationData) => {
      fieldMappings?.push(
        {
          key: field?.name,
          name: field?.attributes?.name,
          ...(field?.attributes?.description && {description: field?.attributes?.description}),
          order: field?.attributes?.type,
          sampling: field?.sampling,
          attributes: this._templateService?.setFieldAttributes(field, 'payload'),
        }
      );
    });
    let payload: Payload = {
      published: true,
      templateName: this.data?.templateDetails?.templateName,
      templateId: this.data?.templateDetails?.templateId,
      payloadType: this.data?.templateDetails?.payloadType,
      description: this.data?.templateDetails?.description,
      payloadSchema: this._templateService?.getRawPayload(),
      sampledFields: this._setSampledFieldArray(),
      fieldMappings: fieldMappings,
      ...(this.data?.csvUrl && { csvUrl: this.data?.csvUrl }),
    };
    if (this.data?.isEdit) {
      this._updateTemplate(payload);
    } else {
      this._createTemplate(payload);
    }
  }

  /**
   * Sets sampled field array
   * @returns sampled field array 
   */
  private _setSampledFieldArray(): string[] {
    if (!this.data?.samplingPossibleKeysArray) {
      return [];
    }
    return this.data?.samplingPossibleKeysArray
      ?.filter((item: SampledKeysArray) => item?.sampling)
      ?.map((item: SampledKeysArray) => item?.key);
  }

  /**
   * Creates template
   * @param payload 
   */
  private _createTemplate(payload: Payload) {
    this._createTemplateSubscription = this._templateService?.createTemplate(payload)?.subscribe({
      next: (result: { details: { templateId: string }, message: string }) => {
        if (!result || !result?.details?.templateId) {
          this._dialogService.showDialog('Info', 'Unable to create template');
          return;
        }
        const { message, details } = result;
        this._dialogService?.showDialog('Success', `${details?.templateId} - ${message}`);
        this.dialogRef?.close();
        this._dataService?.broadcast('resetForm');
        if (sessionStorage.getItem('deviceModuleTenantId')) {
          sessionStorage.removeItem('deviceModuleTenantId');
        };
        this._dataService?.broadcast('switchFlags');
        this._router?.navigate(['/template-management/template-list'], {state: {pageNo: this._sharedService.getPageNumber('template')}});
      },
      error: () => {
        this._dialogService?.showDialog('Error', 'Unable to create template');
      }
    });
  }

  /**
   * Updates template
   * @param payload 
   */
  private _updateTemplate(payload: Payload) {
    this._updateTemplateSubscription = this._templateService?.editTemplate(payload)?.subscribe({
      next: (result: { message: string }) => {
        if (!result) {
          this._dialogService?.showDialog('Info', 'Unable to update template');
          return;
        }
        const { message } = result;
        this._dialogService?.showDialog('Success', `${this.data?.templateDetails?.templateId} - ${message}`);
        this.dialogRef?.close();
        this._dataService?.broadcast('resetForm');
        if (sessionStorage.getItem('deviceModuleTenantId')) {
          sessionStorage.removeItem('deviceModuleTenantId');
        };
        this._dataService?.broadcast('switchFlags');
        this._router?.navigate(['/template-management/template-list'], {state: {pageNo: this._sharedService.getPageNumber('template')}});
      },
      error: () => {
        this._dialogService?.showDialog('Error', 'Unable to update template');
      }
    });
  }

  /**
   * Sets table data
   */
  private _setTableData() {
    this.data?.simulationData?.forEach(field => {
      this.tableData?.push({
        key: field?.name,
        name: field?.attributes?.name,
        description: field?.attributes?.description,
        type: Order[field?.attributes?.type as keyof typeof Order],
        configurations: this._templateService?.setFieldAttributes(field),
      });
    });
  }
}
